/**
 * @module PipelineStageHealth
 * @description Stage-level health table for the Modular pipelines on the Pipeline
 * page. Health is based on version mismatches: each stage's build version and its
 * modpack (module) version, e.g. 1.0.1183.0, are compared with the stage before it
 * (Alpha vs Packaging, Beta vs Alpha, Gamma vs Beta, Production vs Gamma). Any
 * mismatch is flagged straight away, and the version that differs is coloured by the
 * stage's status. Click a row to see the Packaging -> Alpha -> Beta -> Gamma ->
 * Production flow.
 *
 * All health is computed by the backend (GET /pipelines/health -> `stages`); this
 * component only presents it. Each row's `stages` is
 *   { order: string[], stages: { alpha|beta|gamma|prod: {
 *       health, reason, version, upstreamVersion, upstreamName, mismatchSec,
 *       moduleVersion, buildMismatch, moduleMismatch } } }
 * (moduleVersion is null when the stage's module set is not available), or null when
 * the pipeline has no stage data (or could not be read).
 *
 * Rows arrive already grouped by team in the agreed order; "Sort: by team" keeps
 * that order and "worst first" re-sorts by severity.
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, ExternalLink, ArrowRight } from 'lucide-react';

const PIPELINES_BASE_URL = 'https://pipelines.amazon.com/pipelines';
const STAGE_KEYS = ['alpha', 'beta', 'gamma', 'prod'];
const STAGE_NAMES = { alpha: 'Alpha', beta: 'Beta', gamma: 'Gamma', prod: 'Production' };
const HEALTH_LABEL = { healthy: 'Healthy', warning: 'Warning', failing: 'Failing', unknown: 'Unknown' };
const SEVERITY_RANK = { failing: 0, warning: 1, unknown: 2, healthy: 3 };

/** Short human duration for seconds: 5h, 3d. */
export function formatDuration(sec) {
  const h = Math.round((sec || 0) / 3600);
  return h >= 48 ? `${Math.round(h / 24)}d` : `${h}h`;
}

/** Only rows with stage data and a readable status show per-stage health. */
const hasStages = (row) => !!row.stages && row.health !== 'unknown';

/** Worst stage (failing first, then warning) in pipeline order, or null. */
export function worstStage(row) {
  if (!hasStages(row)) return null;
  const { order, stages } = row.stages;
  const key = order.find((k) => stages[k].health === 'failing') || order.find((k) => stages[k].health === 'warning');
  return key ? { key, stage: stages[key] } : null;
}

/** Text for the last table column. */
export function primaryIssue(row) {
  if (row.health === 'unknown') return 'Could not read this pipeline';
  const w = worstStage(row);
  if (w) return `${STAGE_NAMES[w.key]}: ${w.stage.reason}`;
  if (row.health !== 'healthy') return row.reason || 'See pipeline';
  // The backend's reason keeps the "(not rated: …)" note, so a pipeline with unrated stages
  // never reads as plain "in sync" here while its badges say Unknown.
  return hasStages(row) ? row.reason || 'All stages in sync' : 'No stage data';
}

const Badge = ({ health }) => (
  <span className={`psh-badge psh-${health}`}>{HEALTH_LABEL[health] || HEALTH_LABEL.unknown}</span>
);

/** build / module lines; the one that differs from the stage before it takes the stage's colour. */
function VersionLines({ s, className }) {
  const cls = (bad) => `${className} ${bad ? `psh-ver-${s.health === 'failing' ? 'failing' : 'warning'}` : ''}`;
  return (
    <>
      <div className={cls(s.buildMismatch)}>build {s.version || '—'}</div>
      {/* No moduleVersion key = this pipeline has no module to track, so no line at all. A dash
          means it has one but this stage's module set does not show it. */}
      {s.moduleVersion !== undefined && (
        <div className={`${cls(s.moduleMismatch)} psh-mod`}>module {s.moduleVersion || '—'}</div>
      )}
    </>
  );
}

function StageCell({ row, stageKey }) {
  if (!hasStages(row)) return <td><span className="psh-none">—</span></td>;
  const s = row.stages.stages[stageKey];
  if (!s) return <td><span className="psh-none">no stage</span></td>;
  // High level only: the status. The versions and the reason are in the expanded flow.
  return <td><Badge health={s.health} /></td>;
}

/** Connector into a stage, coloured by that stage's status. */
function edgeOf(s) {
  if (s.health === 'unknown') return { cls: 'unknown', label: 'unknown' };
  if (s.health === 'healthy') return { cls: 'healthy', label: 'in sync' };
  if (s.buildMismatch) return { cls: s.health, label: /^Ahead/.test(s.reason || '') ? 'ahead' : 'behind' };
  return { cls: s.health, label: 'module differs' };
}

function Details({ row }) {
  if (!hasStages(row)) {
    return (
      <div className="psh-details">
        <h3>{row.health === 'unknown' ? 'Could not read this pipeline' : 'No stage data'}</h3>
        <p>
          {row.health === 'unknown'
            ? 'Stage versions could not be retrieved. Check pipeline discovery and permissions.'
            : 'This pipeline has no Alpha / Beta / Gamma / Production stages.'}
        </p>
      </div>
    );
  }
  const { order, stages } = row.stages;
  const packagingVersion = stages[order[0]].upstreamVersion;
  return (
    <div className="psh-details">
      <div className="psh-flow">
        <div className="psh-node psh-node-healthy">
          <h4>Packaging</h4>
          <div className="psh-version">build {packagingVersion || '—'}</div>
        </div>
        {order.map((k) => {
          const s = stages[k];
          const e = edgeOf(s);
          return (
            <React.Fragment key={k}>
              <div className={`psh-edge psh-edge-${e.cls}`}>
                <b>{e.label}</b>
                <div className="psh-edge-bar" />
                <ArrowRight size={14} />
              </div>
              <div className={`psh-node psh-node-${s.health}`}>
                <h4>{STAGE_NAMES[k]} <Badge health={s.health} /></h4>
                <VersionLines s={s} className="psh-version" />
                {s.reason && <div className="psh-node-reason">{s.reason}</div>}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** 24 -> "1 day", 72 -> "3 days", 36 -> "36 hours". */
export function formatLimit(hours) {
  if (hours % 24 === 0) return `${hours / 24} day${hours === 24 ? '' : 's'}`;
  return `${hours} hours`;
}

function PipelineStageHealth({ rows, thresholds }) {
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState('all');
  const [sortBy, setSortBy] = useState('team');
  const [expanded, setExpanded] = useState(() => new Set());

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = rows.filter(
      (r) => `${r.name} ${r.owner}`.toLowerCase().includes(q) && (severity === 'all' || r.health === severity),
    );
    if (sortBy === 'severity') {
      return [...list].sort((a, b) => (SEVERITY_RANK[a.health] ?? 2) - (SEVERITY_RANK[b.health] ?? 2));
    }
    return list; // API order: grouped by team
  }, [rows, query, severity, sortBy]);

  const toggle = (name) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const reset = () => { setQuery(''); setSeverity('all'); setSortBy('team'); setExpanded(new Set()); };

  return (
    <div className="psh">
      <div className="psh-tools">
        <input
          className="psh-search"
          placeholder="Search pipelines or teams"
          aria-label="Search pipelines"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select aria-label="Filter by health" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="all">All health statuses</option>
          <option value="failing">Failing</option>
          <option value="warning">Warning</option>
          <option value="healthy">Healthy</option>
          <option value="unknown">Unknown</option>
        </select>
        <select aria-label="Sort order" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="team">Sort: by team</option>
          <option value="severity">Sort: worst first</option>
        </select>
        <button type="button" className="btn btn-sm btn-secondary" onClick={reset}>Reset</button>
      </div>

      <div className="psh-scroll">
        <table className="psh-table">
          <thead>
            <tr>
              <th>Pipeline</th><th>Team</th>
              <th>Alpha</th><th>Beta</th><th>Gamma</th><th>Production</th>
              <th>Overall</th><th>Issue</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8}>No pipelines match this filter.</td></tr>
            )}
            {visible.map((row) => {
              const open = expanded.has(row.name);
              return (
                <React.Fragment key={row.name}>
                  {/* The chevron button is the keyboard-accessible toggle; clicking anywhere else
                      on the row is a mouse shortcut for it. The pipeline name opens the pipeline. */}
                  <tr className="psh-row" onClick={() => toggle(row.name)}>
                    <td>
                      <div className="psh-namecell">
                        <button
                          type="button"
                          className="psh-chevron"
                          aria-expanded={open}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${row.name}`}
                          onClick={(e) => { e.stopPropagation(); toggle(row.name); }}
                        >
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        <a
                          className="psh-pipeline-link"
                          href={`${PIPELINES_BASE_URL}/${row.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in Pipelines"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.name}
                          <ExternalLink size={12} className="psh-link-icon" />
                        </a>
                      </div>
                    </td>
                    <td>{row.owner || '—'}</td>
                    {STAGE_KEYS.map((k) => <StageCell key={k} row={row} stageKey={k} />)}
                    <td><Badge health={row.health} /></td>
                    <td className="psh-issue">{primaryIssue(row)}</td>
                  </tr>
                  {open && (
                    <tr className="psh-detail-row"><td colSpan={8}><Details row={row} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <details className="psh-rules">
        <summary>How health is calculated</summary>
        <p>Each stage is compared with the stage before it: Alpha with Packaging, Beta with Alpha, Gamma with Beta, Production with Gamma. Both the build version and the module (modpack) version are checked. A mismatch is flagged straight away, whatever the time.</p>
        <ul>
          <li><b>Healthy:</b> the build version and the module version match the stage before it.</li>
          <li><b>Warning:</b> the build is behind or ahead of the stage before it (ahead usually means a hotfix or rollback), or the module version differs.</li>
          <li><b>Failing:</b> the build has been behind for longer than the fail limit below.</li>
          <li><b>Overall:</b> the worst stage. The version that differs is shown in the stage's colour. A dash for a module version means that stage's module set is not available yet.</li>
        </ul>
        {thresholds && (
          <table className="psh-thresholds">
            <thead><tr><th>Stage</th><th>Any mismatch</th><th>Failing after</th></tr></thead>
            <tbody>
              {STAGE_KEYS.filter((k) => thresholds[k]).map((k) => (
                <tr key={k}>
                  <td>{STAGE_NAMES[k]}</td>
                  <td>Warning</td>
                  <td>{formatLimit(thresholds[k].fail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="psh-legend">The timer starts when the stage is first seen behind, and restarts when it moves to a new build.</p>
      </details>
    </div>
  );
}

export default PipelineStageHealth;
